import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Building2, Check, ChevronDown, Globe } from 'lucide-react';
import { getCrmTenant, setCrmTenant, CRM_TENANT_CHANGE_EVENT, CRM_TENANT_LANGUAGES } from '../lib/api';
import { useAuth } from '../lib/auth-context';

/**
 * Hook che espone il tenant CRM corrente e si aggiorna quando cambia (via
 * CustomEvent broadcast da `setCrmTenant`).
 * ★ 2026-08-28 (Phase 57.CRM-LANG.4)
 */
export function useCrmTenant() {
  const [tenant, setTenantState] = useState(() => getCrmTenant());
  useEffect(() => {
    const handler = (e) => setTenantState(e.detail?.language || getCrmTenant());
    window.addEventListener(CRM_TENANT_CHANGE_EVENT, handler);
    const storageHandler = (e) => {
      if (e.key === 'crm_tenant_language') setTenantState(getCrmTenant());
    };
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener(CRM_TENANT_CHANGE_EVENT, handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);
  const changeTenant = useCallback((lang) => setCrmTenant(lang), []);
  return { tenant, setTenant: changeTenant };
}

const TENANT_META = {
  global: { flag: '🌐', label: 'Global (aggregato)', short: 'GLOBAL', color: '#E11D2E', icon: Globe },
  it: { flag: '🇮🇹', label: 'Italia', short: 'IT', color: '#008C45' },
  es: { flag: '🇪🇸', label: 'España', short: 'ES', color: '#C60B1E' },
  en: { flag: '🇬🇧', label: 'International', short: 'EN', color: '#012169' },
};

/** Determina se l'utente può vedere il tenant "global" (admin/CEO only). */
function canUseGlobal(user) {
  if (!user) return false;
  const role = String(user.role || '').toLowerCase();
  const isCeoAdmin = Boolean(user.is_ceo || user.is_admin || user.is_super_admin);
  return isCeoAdmin || ['admin', 'ceo', 'super_admin', 'owner'].includes(role);
}

/**
 * CRM Tenant Switcher — selettore globale del CRM.
 * · GLOBAL (admin only) → aggrega IT + ES + EN
 * · IT / ES / EN → filtro stretto per rete locale
 */
export default function CrmTenantSwitcher({ compact = true }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { tenant, setTenant } = useCrmTenant();
  const admin = canUseGlobal(user);
  // ★ Fase 9 — Direttore Commerciale: tenant bloccato lato server (es. 'es')
  const lockedTenant = user?.crm_tenant_locked || null;

  // Se un non-admin ha 'global' selezionato, lo forzo a IT
  useEffect(() => {
    if (!admin && tenant === 'global') setTenant('it');
  }, [admin, tenant, setTenant]);

  // ★ Fase 9 — forza il tenant bloccato (anche se in storage c'era altro)
  useEffect(() => {
    if (lockedTenant && tenant !== lockedTenant) setTenant(lockedTenant);
  }, [lockedTenant, tenant, setTenant]);

  // Lista tenant visibili: 'global' solo se admin
  const visibleTenants = CRM_TENANT_LANGUAGES.filter((code) => code !== 'global' || admin);
  const currentMeta = TENANT_META[tenant] || TENANT_META.it;

  // ★ Fase 9 — badge statico non cliccabile per utenti con tenant bloccato
  if (lockedTenant) {
    const m = TENANT_META[lockedTenant] || TENANT_META.es;
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 px-2.5 md:px-3 border-border cursor-default"
        data-testid="crm-tenant-locked"
        aria-label={`${t('crmTenant.lockedAria', 'CRM dedicato')}: ${m.label}`}
        title={t('crmTenant.lockedTitle', 'Il tuo CRM è dedicato a questo mercato')}
      >
        <span className="text-base leading-none" aria-hidden>{m.flag}</span>
        <span className="hidden md:inline font-semibold text-xs uppercase tracking-widest">
          CRM {m.short}
        </span>
        <span className="md:hidden font-semibold text-xs">{m.short}</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-1.5 px-2.5 md:px-3 border-border ${tenant === 'global' ? 'border-[#E11D2E] bg-[#E11D2E]/10 hover:bg-[#E11D2E]/15' : ''}`}
          data-testid="crm-tenant-switcher"
          aria-label={`CRM tenant: ${currentMeta.label}`}
        >
          <span className="text-base leading-none" aria-hidden>{currentMeta.flag}</span>
          <span className="hidden md:inline font-semibold text-xs uppercase tracking-widest">
            CRM {currentMeta.short}
          </span>
          <span className="md:hidden font-semibold text-xs">{currentMeta.short}</span>
          <ChevronDown className="w-3 h-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2 text-xs">
          <Building2 className="w-3.5 h-3.5" /> {t('crmTenant.title', 'Tenant CRM attivo')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {visibleTenants.map((code) => {
          const m = TENANT_META[code];
          const active = code === tenant;
          const isGlobal = code === 'global';
          return (
            <DropdownMenuItem
              key={code}
              onClick={() => setTenant(code)}
              className={`gap-2 cursor-pointer ${active ? 'bg-accent/50' : ''} ${isGlobal ? 'font-semibold' : ''}`}
              data-testid={`crm-tenant-option-${code}`}
            >
              <span className="text-base leading-none" aria-hidden>{m.flag}</span>
              <div className="flex-1">
                <div className="text-sm font-medium">{m.label}</div>
                <div className={`text-[10px] uppercase tracking-widest ${isGlobal ? 'text-[#E11D2E] font-bold' : 'text-muted-foreground'}`}>
                  {isGlobal ? 'ADMIN · Tutti i tenant' : `CRM ${m.short}`}
                </div>
              </div>
              {active && <Check className="w-4 h-4 text-primary" strokeWidth={2.5} />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[10px] text-muted-foreground leading-relaxed">
          {tenant === 'global'
            ? '⚡ Vista globale: aggreghi lead, prodotti, proposte di IT + ES + EN. Solo admin.'
            : t('crmTenant.hint', 'Ogni tenant vede solo lead, prodotti e proposte della propria lingua. La segmentazione è centralizzata.')}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
